import React from 'react';
import { DD_SECTION_DESC, DD_SECTION_TITLE } from '../../components/investigation/feedCardStyles';
import { evaluateBreachesRiskPillars } from '../../utils/workspaceRiskLevel';

/**
 * Dynamic Breaches & Risk package — rose when any of 5 pillars fires, else emerald.
 *
 * @param {{
 *   displayResults?: object[],
 *   children?: React.ReactNode,
 *   loading?: boolean,
 *   emptyHint?: string,
 *   cardCount?: number,
 * }} props
 */
export default function BreachesRiskSection({
  displayResults = [],
  children = null,
  loading = false,
  emptyHint = 'No sanctions, breach, or dark-web signals.',
  cardCount = 0,
}) {
  const { hasIssue, totalThreats, pillars } = evaluateBreachesRiskPillars(displayResults);
  const shell = hasIssue
    ? 'border-rose-500 bg-rose-950/20'
    : 'border-emerald-500/30 bg-emerald-950/10';

  return (
    <section
      id="corp-section-breaches_risk"
      data-corp-section="corp-section-breaches_risk"
      data-testid="corp-section-breaches-risk"
      data-risk-state={hasIssue ? 'critical' : 'clean'}
      className={`scroll-mt-20 overflow-hidden rounded-2xl border shadow-sm ${shell}`}
    >
      <div className="border-b border-white/5 px-3 pt-3 pb-2.5 sm:px-4 sm:pt-4 sm:pb-3">
        <h3 className={hasIssue ? DD_SECTION_TITLE.rose : DD_SECTION_TITLE.emerald}>
          Breaches &amp; Risk
        </h3>
        <p className={DD_SECTION_DESC}>
          {hasIssue
            ? `Active risk across ${totalThreats} pillar${totalThreats === 1 ? '' : 's'} · ${cardCount} card${cardCount === 1 ? '' : 's'}`
            : `All five pillars clean · ${cardCount} card${cardCount === 1 ? '' : 's'}`}
        </p>
        <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Risk pillars">
          {[
            { key: 'sanctions', label: 'Sanctions', on: pillars.sanctions },
            { key: 'breaches', label: 'HIBP', on: pillars.breaches },
            { key: 'darkweb', label: 'Dark Web', on: pillars.darkweb },
            { key: 'domainSecurity', label: 'Domain Security', on: pillars.domainSecurity },
            { key: 'virusTotal', label: 'VirusTotal', on: pillars.virusTotal },
          ].map((p) => (
            <li
              key={p.key}
              className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                p.on
                  ? 'border-rose-400/50 bg-rose-950/40 text-rose-200'
                  : 'border-emerald-500/25 bg-emerald-950/20 text-emerald-300/80'
              }`}
            >
              {p.label}
            </li>
          ))}
        </ul>
      </div>
      <div className="min-w-0 space-y-3 overflow-x-clip p-3 sm:p-4" data-testid="corp-section-breaches-risk-cards">
        {children}
        {!loading && cardCount === 0 ? (
          <p className="text-xs text-slate-500">{emptyHint}</p>
        ) : null}
      </div>
    </section>
  );
}
